/**
 * useWebVitals - Web Vitals 监测 Hook
 *
 * 🎯 功能：
 * 1. 监测所有核心 Web Vitals 指标
 * 2. 控制台输出优雅的性能数据（带元素引用）
 * 3. 触发自定义事件供 Panel 组件显示
 *
 * 📊 监测指标：
 * - LCP (Largest Contentful Paint) - 最大内容绘制时间
 * - CLS (Cumulative Layout Shift) - 累积布局偏移
 * - INP (Interaction to Next Paint) - 交互响应时间
 * - FCP (First Contentful Paint) - 首次内容绘制时间
 * - TTFB (Time to First Byte) - 首字节时间
 */

import { useEffect } from 'react';
import { onCLS, onFCP, onLCP, onTTFB, onINP, type Metric } from 'web-vitals';

export function useWebVitals() {
  useEffect(() => {
    // 只在浏览器环境执行
    if (typeof window === 'undefined') {
      return;
    }

    // 标记：防止重复初始化
    if ((window as any).__webVitalsInitialized) {
      return;
    }
    (window as any).__webVitalsInitialized = true;

    /**
     * 优雅的控制台输出
     */
    const logMetric = (metric: Metric) => {
      const emoji = {
        LCP: '🎨',
        FCP: '🖼️',
        CLS: '📐',
        INP: '⚡',
        TTFB: '🚀',
      }[metric.name] || '📊';

      const ratingEmoji = {
        good: '✅',
        'needs-improvement': '⚠️',
        poor: '❌',
      }[metric.rating] || '📊';

      const value = metric.name === 'CLS' ?
        metric.value.toFixed(3) :
        `${Math.round(metric.value)}ms`;

      // 提取关联元素
      let element = null;
      if (metric.entries && metric.entries.length > 0) {
        const entry = metric.entries[metric.entries.length - 1] as any;

        // 不同指标的元素位置不同
        if (metric.name === 'CLS' && entry.sources && entry.sources.length > 0) {
          element = entry.sources[0].node;
        } else {
          element = entry.element || entry.target || entry.node;
        }
      }

      // 使用 console.group 创建可折叠的优雅输出
      console.groupCollapsed(
        `%c${emoji} Web Vitals: ${metric.name}%c ${value} %c${ratingEmoji} ${metric.rating}`,
        'color: #2563eb; font-weight: bold; font-size: 13px;',
        'color: #059669; font-weight: bold; font-size: 13px;',
        'color: #6b7280; font-size: 12px;'
      );

      // 输出元素详情（如果有）
      if (element) {
        console.log('元素:', element);

        const el = element as Element;
        if (el.tagName) {
          const tagName = el.tagName.toLowerCase();
          const className = el.className || '';
          const id = el.id || '';
          const textContent = (el.textContent || '').substring(0, 100).trim();

          console.log('%c元素详情', 'color: #8b5cf6; font-weight: bold;');
          console.table({
            元素标签: `<${tagName}>`,
            class: className || 'N/A',
            id: id || 'N/A',
            '文本内容 (前100字符)': textContent || 'N/A',
          });
        }
      }

      // 输出性能数据表格
      console.table({
        指标: metric.name,
        数值: value,
        评级: metric.rating,
        目标:
          metric.name === 'LCP' ? '< 2500ms' :
          metric.name === 'FCP' ? '< 1800ms' :
          metric.name === 'CLS' ? '< 0.1' :
          metric.name === 'INP' ? '< 200ms' :
          metric.name === 'TTFB' ? '< 800ms' : 'N/A',
        ID: metric.id,
        导航类型: metric.navigationType,
      });

      if (metric.entries && metric.entries.length > 0) {
        console.log('📋 性能条目:', metric.entries);
      }

      console.groupEnd();
    };

    /**
     * LCP 处理器：控制台输出 + 触发可视化事件
     */
    const handleLCP = (metric: Metric) => {
      logMetric(metric);

      let element = null;
      if (metric.entries && metric.entries.length > 0) {
        const lcpEntry = metric.entries[metric.entries.length - 1] as any;
        element = lcpEntry.element;
      }

      window.dispatchEvent(
        new CustomEvent('web-vitals-update', {
          detail: {
            name: metric.name,
            value: metric.value,
            rating: metric.rating,
            delta: metric.delta,
            element: element,
            entries: metric.entries,
          },
        })
      );
    };

    /**
     * 其他指标处理器：仅控制台输出
     */
    const handleMetric = (metric: Metric) => {
      logMetric(metric);

      let element = null;
      if (metric.entries && metric.entries.length > 0) {
        const entry = metric.entries[metric.entries.length - 1] as any;
        element = entry.element || entry.target || entry.node;
      }

      window.dispatchEvent(
        new CustomEvent('web-vitals-update', {
          detail: {
            name: metric.name,
            value: metric.value,
            rating: metric.rating,
            delta: metric.delta,
            element: element,
            entries: metric.entries,
          },
        })
      );
    };

    // 监测所有核心 Web Vitals 指标
    onLCP(handleLCP, { reportAllChanges: true }); // LCP - 实时报告
    onCLS(handleMetric, { reportAllChanges: true }); // CLS - 实时报告每次变化
    onINP(handleMetric, { reportAllChanges: true }); // INP - 实时报告每次交互
    onFCP(handleMetric); // FCP - 仅控制台
    onTTFB(handleMetric); // TTFB - 仅控制台

    // 清理函数
    return () => {
      (window as any).__webVitalsInitialized = false;
    };
  }, []);
}
